import { DentalStoryCount } from "./DentalStoryCount";
import { weeklyFrontDeskHoursLabel } from "./dentalImpactFigures";
import { ArrowDown, CalendarCheck2, Clock3, Moon, PhoneIncoming, PhoneMissed, PhoneOutgoing, ThumbsUp } from "lucide-react";

// Approved local design-reference figures, not measured ClawEase results.
// Source: https://www.denti.ai/ai-receptionist (vendor-reported).
// Replace with substantiated ClawEase results before public publication.
const practiceChallenges = [
  { icon: PhoneMissed, value: "1 in 3", label: "incoming dental calls", detail: "go unanswered" },
  { icon: PhoneOutgoing, value: "75%", label: "of missed calls", detail: "never call back" },
  { icon: Moon, value: "40%", label: "of appointment requests", detail: "happen after hours" },
];

const benefits = [
  { icon: PhoneIncoming, value: "80%", label: "fewer", detail: "missed calls" },
  { icon: CalendarCheck2, value: "25%", label: "more appointments", detail: "booked" },
  { icon: Clock3, value: weeklyFrontDeskHoursLabel, label: "saved per week for", detail: "the front desk" },
  { icon: ThumbsUp, value: "Better", label: "patient", detail: "experience" },
  { icon: Moon, value: "100%", label: "after-hours", detail: "coverage" },
];

export function DentalFrontDeskStory() {
  return (
    <section id="front-desk-story" aria-labelledby="front-desk-story-title" className="scroll-mt-24 bg-[#fbfaf6] px-5 py-12 sm:px-10 sm:py-14 lg:px-12">
      <div className="dental-content">
        <header className="mb-6 text-center">
          <h2 id="front-desk-story-title" className="text-[30px] font-extrabold leading-[1.12] tracking-[-.048em] text-[#172033] sm:text-[38px]">
            A busy front desk. A better way forward.
          </h2>
        </header>

        <div className="space-y-5">
          <div className="rounded-[22px] border border-[#e9e5e7] bg-[#faf9f8] px-5 py-6 shadow-[0_8px_24px_rgba(35,28,43,.02)] sm:px-7">
            <h3 className="text-center text-[16px] font-bold tracking-[-.025em] text-[#172033]">
              The front-desk reality
            </h3>
            <dl className="mt-5 grid gap-6 md:grid-cols-3 md:gap-5">
              {practiceChallenges.map(({ icon: Icon, value, label, detail }) => (
                <div key={value} className="grid grid-cols-[30px_minmax(0,1fr)] gap-x-3 justify-self-center lg:gap-x-4">
                  <Icon aria-hidden="true" strokeWidth={1.3} className="mt-1 h-[30px] w-[30px] text-[#82838a]" />
                  <dt className="whitespace-nowrap text-[34px] font-extrabold leading-none tracking-[-.045em] text-[#172033] sm:text-[38px]"><DentalStoryCount value={value} /></dt>
                  <dd className="col-start-2 mt-2 text-[15px] leading-6 text-[#596176]">
                    <span className="block">{label}</span>
                    <span className="block">{detail}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-[22px] border border-[#e8e1eb] bg-white px-4 py-6 shadow-[0_12px_30px_-14px_rgba(109,53,207,.12),0_3px_10px_rgba(35,28,43,.02)] sm:px-7">
            <h3 className="px-1 text-[20px] font-bold leading-tight tracking-[-.035em] text-[#172033]">
              Practice Impact with ClawEase
            </h3>
            <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-5 lg:gap-x-5">
              {benefits.map(({ icon: Icon, value, label, detail }, index) => (
                <li key={label} className={`flex min-w-0 flex-col items-center text-center ${index === benefits.length - 1 ? "col-span-2 sm:col-span-1" : ""}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-[13px] border border-[#eee7f7] bg-[#f7f3fb]">
                    <Icon aria-hidden="true" strokeWidth={1.4} className="h-6 w-6 text-[#6746a7]" />
                  </span>
                  <strong className="mt-4 block whitespace-nowrap text-[30px] font-extrabold leading-none tracking-[-.045em] text-[#6d35cf] sm:text-[34px]"><DentalStoryCount value={value} /></strong>
                  <p className="mt-2 text-[15px] leading-6 text-[#596176]">
                    <span className="block">{label}</span>
                    <span className="block">{detail}</span>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href="#dental-practice-impact" className="group inline-flex max-w-full flex-col items-center gap-3 rounded-lg px-3 py-2 text-[14px] leading-6 text-[#596176] no-underline transition-colors hover:text-[#6d35cf] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6d35cf] focus-visible:ring-offset-4 focus-visible:ring-offset-[#fbfaf6]">
            <span>See how ClawEase helps your dental practice</span>
            <ArrowDown aria-hidden="true" strokeWidth={1.6} className="dental-scroll-cue h-6 w-6 text-[#6d35cf]" />
          </a>
        </div>
      </div>
    </section>
  );
}
